/**
 * Marketplace Category API Client
 * Handles all category-related API calls for marketplace integration
 */

import axios, { AxiosError } from 'axios'
import { env } from '../../config/env'
import * as ApiSettingService from '../../db/serviceApiSetting'

// Response interfaces matching API documentation
export interface CategorySummary {
  id: string
  name: string
  hasChildren: boolean
  isActive: boolean
  parentCategoryId?: string
}

export interface CategoryListResponse {
  success: boolean
  data: {
    categories: CategorySummary[]
  }
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
  meta: {
    marketplace: string
    timestamp: string
    requestId: string
  }
}

export interface AttributeValue {
  valueId: number
  originalValueName: string
  displayValueName: string
  valueUnit?: string
}

export interface AttributeConfiguration {
  attributeId: number
  originalAttributeName: string
  displayAttributeName: string
  isMandatory: boolean
  inputType: 'DROP_DOWN' | 'MULTIPLE_SELECT' | 'TEXT_FILED' | 'COMBO_BOX' | 'MULTIPLE_SELECT_COMBO_BOX'
  formatType?: 'NORMAL' | 'QUANTITATIVE'
  inputValidation?: 'STRING_TYPE' | 'INT_TYPE' | 'FLOAT_TYPE' | 'ENUM_TYPE' | 'DATE_TYPE' | 'TIMESTAMP_TYPE'
  attributeValues?: AttributeValue[]
}

export interface CategoryAttributesResponse {
  success: boolean
  data: {
    categoryId: string
    categoryName: string
    attributes: AttributeConfiguration[]
  }
  meta: {
    marketplace: string
    timestamp: string
    requestId: string
  }
}

export interface CategoryOptions {
  page?: number
  limit?: number
  search?: string
}

export type MarketplaceType = 'shopee' | 'lazada' | 'tiktok'

// Error response interface
interface ErrorResponse {
  success: false
  error: {
    code: string
    message: string
    details?: Array<{
      field: string
      message: string
    }>
  }
  meta: {
    timestamp: string
    requestId: string
  }
}

class MarketplaceCategoryClient {
  private baseUrl: string
  private apiVersion: string

  constructor() {
    this.baseUrl = env.SERVER_URL || ''
    this.apiVersion = 'api/v2'
  }

  /**
   * Get JWT token for authentication
   */
  private async getAuthToken(): Promise<string> {
    try {
      const accountAccess = await ApiSettingService.getAccess()
      if (!accountAccess?.accessToken) {
        throw new Error('Failed to get access token')
      }
      return accountAccess.accessToken
    } catch (error) {
      console.error('Error getting auth token:', error)
      throw new Error('Authentication failed')
    }
  }

  /**
   * Make authenticated API request
   */
  private async makeRequest<T>(endpoint: string): Promise<T> {
    try {
      const token = await this.getAuthToken()
      const url = `${this.baseUrl}/${this.apiVersion}${endpoint}`
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 15000, // 15 second timeout
      })

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>
        if (axiosError.response?.data?.error) {
          throw new Error(axiosError.response.data.error.message)
        }
        if (axiosError.response?.status === 401) {
          throw new Error('Authentication failed - please login again')
        }
        if (axiosError.response?.status === 404) {
          throw new Error('Resource not found')
        }
        if (axiosError.code === 'ECONNABORTED') {
          throw new Error('Request timeout - please try again')
        }
      }
      throw new Error('Failed to fetch data from server')
    }
  }

  /**
   * Get paginated category list
   */
  async getCategories(
    marketplace: MarketplaceType,
    options: CategoryOptions = {}
  ): Promise<CategoryListResponse> {
    const { page = 1, limit = 50, search } = options

    // Validate parameters
    if (page < 1) {
      throw new Error('Page must be a positive integer')
    }
    if (limit < 1 || limit > 100) {
      throw new Error('Limit must be between 1 and 100')
    }

    let endpoint = `/marketplace/${marketplace}/categories?page=${page}&limit=${limit}`
    
    if (search && search.trim().length > 0) {
      endpoint += `&search=${encodeURIComponent(search.trim())}`
    }

    return this.makeRequest<CategoryListResponse>(endpoint)
  }

  /**
   * Get category attributes for dynamic form generation
   */
  async getCategoryAttributes(
    marketplace: MarketplaceType,
    categoryId: string
  ): Promise<CategoryAttributesResponse> {
    if (!categoryId || categoryId.trim().length === 0) {
      throw new Error('Category ID is required')
    }

    const endpoint = `/marketplace/${marketplace}/categories/${categoryId}/attributes`
    return this.makeRequest<CategoryAttributesResponse>(endpoint)
  }

  /**
   * Search categories with debounce support
   */
  async searchCategories(
    marketplace: MarketplaceType,
    searchTerm: string,
    limit: number = 20
  ): Promise<CategoryListResponse> {
    if (!searchTerm || searchTerm.trim().length < 2) {
      throw new Error('Search term must be at least 2 characters')
    }

    return this.getCategories(marketplace, { search: searchTerm, limit, page: 1 })
  }

  /**
   * Get root categories by filtering client-side
   * Since API only provides flat category list, we filter for categories without parentCategoryId
   */
  async getRootCategories(
    marketplace: MarketplaceType,
    options: CategoryOptions = {}
  ): Promise<CategoryListResponse> {
    // Get all categories and filter for root categories (no parentCategoryId)
    const response = await this.getCategories(marketplace, options)
    
    // Filter for root categories only
    const rootCategories = response.data.categories.filter(cat => !cat.parentCategoryId)
    
    return {
      ...response,
      data: {
        categories: rootCategories
      },
      pagination: {
        ...response.pagination,
        total: rootCategories.length
      }
    }
  }

  /**
   * Get child categories by filtering client-side
   * Since API only provides flat category list, we filter by parentCategoryId
   */
  async getCategoriesByParent(
    marketplace: MarketplaceType,
    parentCategoryId: string,
    options: CategoryOptions = {}
  ): Promise<CategoryListResponse> {
    if (!parentCategoryId || parentCategoryId.trim().length === 0) {
      throw new Error('Parent category ID is required')
    }

    // Get all categories and filter for children of specified parent
    const response = await this.getCategories(marketplace, options)
    
    // Filter for children of the specified parent
    const childCategories = response.data.categories.filter(cat => cat.parentCategoryId === parentCategoryId)
    
    return {
      ...response,
      data: {
        categories: childCategories
      },
      pagination: {
        ...response.pagination,
        total: childCategories.length
      }
    }
  }

  /**
   * Get all categories across all pages (use with caution for large datasets)
   */
  async getAllCategories(
    marketplace: MarketplaceType,
    search?: string
  ): Promise<CategorySummary[]> {
    const allCategories: CategorySummary[] = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      try {
        const response = await this.getCategories(marketplace, { 
          page, 
          limit: 100, // Max limit for efficiency
          search 
        })

        allCategories.push(...response.data.categories)

        hasMore = page < response.pagination.totalPages
        page++
      } catch (error) {
        console.error(`Failed to fetch page ${page}:`, error)
        break
      }
    }

    return allCategories
  }
}

// Export singleton instance
export const categoryClient = new MarketplaceCategoryClient()

// Export class for testing
export default MarketplaceCategoryClient